// The console's routes. Everything but sign-in needs a session: the authed layout loads /v1/me
// and sends a signed-out visitor to sign-in, with the page to come back to.
import type { QueryClient } from '@tanstack/react-query'
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  notFound,
  Outlet,
  redirect,
} from '@tanstack/react-router'
import { isApiError } from './api/client'
import { meQuery, projectsQuery } from './api/queries'
import type { Me } from './api/types'
import { RootLayout } from './components/RootLayout'
import { Shell } from './components/Shell'
import { AgentsPage } from './routes/Agents'
import { AlertRulePage, AlertsPage } from './routes/Alerts'
import { BillingReturnPage } from './routes/BillingReturn'
import { DashboardsPage } from './routes/Dashboards'
import { ExplorePage } from './routes/Explore'
import { FlagPage, FlagsPage } from './routes/Flags'
import { FramesPage } from './routes/Frames'
import { HealthPage } from './routes/Health'
import { HealthExitsPage } from './routes/HealthExits'
import { HomePage } from './routes/Home'
import { InstrumentationPage } from './routes/Instrumentation'
import { InvitationAcceptPage } from './routes/InvitationAccept'
import { IssuePage } from './routes/Issue'
import { IssuesPage } from './routes/Issues'
import { JourneysPage } from './routes/Journeys'
import { LogsPage } from './routes/Logs'
import { EndpointPage, NetworkPage, TracePage } from './routes/Network'
import { NotFoundPage } from './routes/NotFound'
import { NewProjectPage, WelcomePage } from './routes/Onboarding'
import { PeoplePage, PersonPage } from './routes/People'
import { HangsPage, PerformancePage, StartPage } from './routes/Performance'
import { ReleasePage } from './routes/Release'
import { ReleasesPage } from './routes/Releases'
import { ResourcesPage } from './routes/Resources'
import { ScreenPage, ScreensPage } from './routes/Screens'
import { SessionPage } from './routes/Session'
import { SessionsPage } from './routes/Sessions'
import { SettingsPage } from './routes/Settings'
import { SetupPage } from './routes/Setup'
import { SignalsPage } from './routes/Signals'
import { SignalsRulePage } from './routes/SignalsRule'
import { SignInPage } from './routes/SignIn'
import { SignupPage } from './routes/Signup'
import {
  isSettingsSection,
  parseAlertsSearch,
  parseDashboardsSearch,
  parseEndpointSearch,
  parseExploreSearch,
  parseFramesSearch,
  parseInvitationSearch,
  parseIssuesSearch,
  parseJourneysSearch,
  parseLogsSearch,
  parseNetworkSearch,
  parsePerfSearch,
  parsePersonSearch,
  parsePlanSearch,
  parseReleasesListSearch,
  parseReleasesSearch,
  parseScopeSearch,
  parseScreenSearch,
  parseScreensSearch,
  parseSessionsSearch,
  parseSettingsSearch,
  parseSignInSearch,
  parseStartSearch,
  type ScopeSearch,
} from './routes/search'
import { type SandboxState, sandboxForPath, startSandbox } from './sandbox'

export interface RouterContext {
  queryClient: QueryClient
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFoundPage,
})

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  validateSearch: parseSignInSearch,
  component: SignInPage,
})

/** The signed-in user, or a redirect to sign-in that comes back here. */
async function requireMe(queryClient: QueryClient, href: string): Promise<Me> {
  try {
    return await queryClient.ensureQueryData(meQuery)
  } catch (error) {
    if (isApiError(error, 401)) throw redirect({ to: '/sign-in', search: { return_to: href } })
    throw error
  }
}

const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  beforeLoad: async ({ context, location }): Promise<{ me: Me; sandbox?: SandboxState }> => {
    // Every page needs a session, the sandbox's too (D105): it opens from inside the console.
    const me = await requireMe(context.queryClient, location.href)
    const sandbox = sandboxForPath(location.pathname)
    return sandbox ? { me, sandbox: await startSandbox() } : { me }
  },
  component: Outlet,
})

const indexRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/',
  beforeLoad: ({ context }) => {
    const org = context.me?.orgs[0]
    if (!org) throw redirect({ to: '/welcome' })
    throw redirect({ to: '/o/$orgId', params: { orgId: org.id } })
  },
})

/**
 * The invitation email's link (APS-274): signed in first (sign-in comes back here with the
 * token), then the invitation is accepted and the organization opens.
 */
const invitationAcceptRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/invitations/accept',
  validateSearch: parseInvitationSearch,
  component: InvitationAcceptPage,
})

const welcomeRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/welcome',
  validateSearch: parsePlanSearch,
  component: WelcomePage,
})

/** The public sign-up link (the site): sign-in on its sign-up path, then on (Signup.tsx). */
const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  validateSearch: parsePlanSearch,
  component: SignupPage,
})

const orgRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/o/$orgId',
  component: Outlet,
})

const orgIndexRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: '/',
  beforeLoad: async ({ context, params }) => {
    const projects = await context.queryClient.ensureQueryData(projectsQuery(params.orgId))
    const first = projects[0]
    if (!first) throw redirect({ to: '/o/$orgId/new-project', params })
    throw redirect({
      to: '/o/$orgId/p/$projectId/home',
      params: { orgId: params.orgId, projectId: first.id },
    })
  },
})

/** Where Stripe's billing portal returns (D88 addendum): BillingReturnPage checks, then moves on. */
const billingReturnRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: '/billing-return',
  component: BillingReturnPage,
})

const newProjectRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: '/new-project',
  validateSearch: parsePlanSearch,
  component: NewProjectPage,
})

const projectRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: '/p/$projectId',
  component: Shell,
})

const projectIndexRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/o/$orgId/p/$projectId/home', params })
  },
})

const homeRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/home',
  validateSearch: parseScopeSearch,
  component: HomePage,
})

const releasesRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/releases',
  validateSearch: parseReleasesListSearch,
  component: ReleasesPage,
})

/** Health (APS-324): one app's active devices, adoption and stability by release and kind. */
const healthRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/health',
  validateSearch: parseReleasesSearch,
  component: HealthPage,
})

/** App exits (APS-417, D113 phase 1): exits by reason per release and per day, and their sources. */
const healthExitsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/health/exits',
  validateSearch: parseReleasesSearch,
  component: HealthExitsPage,
})

const releaseRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/releases/$release',
  validateSearch: parseReleasesSearch,
  component: ReleasePage,
})

const issuesRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/issues',
  validateSearch: parseIssuesSearch,
  component: IssuesPage,
})

const issueRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/issues/$issueId',
  validateSearch: parseScopeSearch,
  component: IssuePage,
})

// App flags (Console D, APS-325): under Users in the sidebar (D121).
const flagsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/flags',
  validateSearch: parseScopeSearch,
  component: FlagsPage,
})

const flagRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/flags/$name',
  validateSearch: parseScopeSearch,
  component: FlagPage,
})

const sessionsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/sessions',
  validateSearch: parseSessionsSearch,
  component: SessionsPage,
})

// People (D114 #6, APS-421): an app user by the user.hash the app sends, and the team's pins.
const peopleRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/users',
  validateSearch: parseScopeSearch,
  component: PeoplePage,
})

const personRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/users/$userHash',
  validateSearch: parsePersonSearch,
  component: PersonPage,
})

const sessionRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/sessions/$sessionId',
  validateSearch: parseScopeSearch,
  component: SessionPage,
})

// Performance (plan step 3.4): overview, app start, screens, network, hangs, and a trace.
const performanceRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/performance',
  validateSearch: parsePerfSearch,
  component: PerformancePage,
})

const startPerfRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/performance/start',
  validateSearch: parseStartSearch,
  component: StartPage,
})

const screensRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/performance/screens',
  validateSearch: parseScreensSearch,
  component: ScreensPage,
})

const screenRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/performance/screens/detail',
  validateSearch: parseScreenSearch,
  component: ScreenPage,
})

const networkRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/performance/network',
  validateSearch: parseNetworkSearch,
  component: NetworkPage,
})

const endpointRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/performance/network/endpoint',
  validateSearch: parseEndpointSearch,
  component: EndpointPage,
})

const hangsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/performance/hangs',
  validateSearch: parsePerfSearch,
  component: HangsPage,
})

const framesRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/performance/frames',
  validateSearch: parseFramesSearch,
  component: FramesPage,
})

// Resources (D113 phase 2, APS-462): memory, battery, thermal state and storage.
const resourcesRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/performance/resources',
  validateSearch: parsePerfSearch,
  component: ResourcesPage,
})

// Journeys (plan step 3.6): funnels and paths over screens.
const journeysRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/journeys',
  validateSearch: parseJourneysSearch,
  component: JourneysPage,
})

const traceRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/traces/$traceId',
  validateSearch: parseScopeSearch,
  component: TracePage,
})

const setupRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/setup',
  validateSearch: parseScopeSearch,
  component: SetupPage,
})

const exploreRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/explore',
  validateSearch: parseExploreSearch,
  component: ExplorePage,
})

// Logs (APS-327): the project's logs across every session.
const logsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/logs',
  validateSearch: parseLogsSearch,
  component: LogsPage,
})

const dashboardsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/dashboards',
  validateSearch: parseDashboardsSearch,
  component: DashboardsPage,
})

/** Settings, one page a section (Console D's settings mode); an unknown section is not found. */
const settingsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/settings/$section',
  validateSearch: parseSettingsSearch,
  beforeLoad: ({ params }) => {
    if (!isSettingsSection(params.section)) throw notFound()
  },
  component: SettingsPage,
})

const signalsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/signals',
  validateSearch: parseScopeSearch,
  component: SignalsPage,
})

// What the SDK does on the devices (D114, APS-428): the instruments, `?instrument=` the one shown.
const instrumentationRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/instrumentation',
  validateSearch: (s: Record<string, unknown>): ScopeSearch & { instrument?: string } => ({
    ...parseScopeSearch(s),
    ...(typeof s.instrument === 'string' && /^[a-z]{1,32}$/.test(s.instrument)
      ? { instrument: s.instrument }
      : {}),
  }),
  component: InstrumentationPage,
})

// A rule of an app's policy (D114, APS-428): `new` for a new one, `?template=` to start from one.
const signalsRuleRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/signals/rules/$ruleId',
  validateSearch: (s: Record<string, unknown>): ScopeSearch & { template?: string } => ({
    ...parseScopeSearch(s),
    ...(typeof s.template === 'string' && /^[a-z0-9_]{1,32}$/.test(s.template)
      ? { template: s.template }
      : {}),
  }),
  component: SignalsRulePage,
})

const agentsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/agents',
  validateSearch: parseScopeSearch,
  component: AgentsPage,
})

// Alerts (Console D's #/alerts): the project's rules, out of Settings (APS-309).
const alertsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/alerts',
  validateSearch: parseAlertsSearch,
  component: AlertsPage,
})

const alertRuleRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/alerts/$ruleId',
  validateSearch: parseScopeSearch,
  component: AlertRulePage,
})

export const routeTree = rootRoute.addChildren([
  signInRoute,
  signupRoute,
  authedRoute.addChildren([
    indexRoute,
    welcomeRoute,
    invitationAcceptRoute,
    orgRoute.addChildren([
      orgIndexRoute,
      billingReturnRoute,
      newProjectRoute,
      projectRoute.addChildren([
        projectIndexRoute,
        homeRoute,
        releasesRoute,
        healthRoute,
        healthExitsRoute,
        releaseRoute,
        issuesRoute,
        issueRoute,
        flagsRoute,
        flagRoute,
        sessionsRoute,
        sessionRoute,
        peopleRoute,
        personRoute,
        exploreRoute,
        logsRoute,
        dashboardsRoute,
        performanceRoute,
        startPerfRoute,
        screensRoute,
        screenRoute,
        networkRoute,
        endpointRoute,
        hangsRoute,
        framesRoute,
        resourcesRoute,
        journeysRoute,
        traceRoute,
        setupRoute,
        settingsRoute,
        agentsRoute,
        instrumentationRoute,
        signalsRoute,
        signalsRuleRoute,
        alertsRoute,
        alertRuleRoute,
      ]),
    ]),
  ]),
])

export function makeRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    scrollRestoration: true,
  })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof makeRouter>
  }
}
